// RRAASI Coin Feature Costs Configuration
// This file defines how many RRAASI coins each feature costs

export interface FeatureCost {
  id: string
  name: string
  description: string
  cost: number
  category: 'chat' | 'analysis' | 'prediction' | 'premium' | 'bonus'
  freeTierAvailable: boolean
  subscriptionUnlimited: boolean
}

export const FEATURE_COSTS: FeatureCost[] = [
  // Chat Features
  {
    id: 'basic_chat',
    name: 'Basic Chat',
    description: 'General astrology questions and guidance',
    cost: 5,
    category: 'chat',
    freeTierAvailable: true,
    subscriptionUnlimited: true
  },
  {
    id: 'advanced_chat',
    name: 'Advanced Chat',
    description: 'Detailed personal readings and analysis',
    cost: 10,
    category: 'chat',
    freeTierAvailable: false,
    subscriptionUnlimited: true
  },
  {
    id: 'expert_chat',
    name: 'Expert Consultation',
    description: 'Professional astrologer consultation',
    cost: 25,
    category: 'chat',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },

  // Analysis Features
  {
    id: 'birth_chart',
    name: 'Birth Chart',
    description: 'Detailed natal chart analysis',
    cost: 15,
    category: 'analysis',
    freeTierAvailable: true,
    subscriptionUnlimited: true
  },
  {
    id: 'compatibility_check',
    name: 'Compatibility Check',
    description: 'Relationship compatibility analysis',
    cost: 20,
    category: 'analysis',
    freeTierAvailable: true,
    subscriptionUnlimited: true
  },
  {
    id: 'career_guidance',
    name: 'Career Guidance',
    description: 'Professional and career path analysis',
    cost: 30,
    category: 'analysis',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },

  // Prediction Features
  {
    id: 'daily_horoscope',
    name: 'Daily Horoscope',
    description: 'Personalized daily predictions',
    cost: 3,
    category: 'prediction',
    freeTierAvailable: true,
    subscriptionUnlimited: true
  },
  {
    id: 'weekly_forecast',
    name: 'Weekly Forecast',
    description: 'Weekly predictions and insights',
    cost: 8,
    category: 'prediction',
    freeTierAvailable: false,
    subscriptionUnlimited: true
  },
  {
    id: 'monthly_forecast',
    name: 'Monthly Forecast',
    description: 'Monthly predictions and planning',
    cost: 15,
    category: 'prediction',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },

  // Premium Features
  {
    id: 'custom_report',
    name: 'Custom Report',
    description: 'Personalized detailed report',
    cost: 50,
    category: 'premium',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },
  {
    id: 'relationship_analysis',
    name: 'Relationship Analysis',
    description: 'In-depth relationship insights',
    cost: 40,
    category: 'premium',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },
  {
    id: 'life_path_reading',
    name: 'Life Path Reading',
    description: 'Comprehensive life path analysis',
    cost: 75,
    category: 'premium',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  },

  // Bonus Features
  {
    id: 'meditation_guide',
    name: 'Meditation Guide',
    description: 'Personalized meditation practices',
    cost: 10,
    category: 'bonus',
    freeTierAvailable: false,
    subscriptionUnlimited: true
  },
  {
    id: 'crystal_recommendations',
    name: 'Crystal Recommendations',
    description: 'Personal crystal and gemstone guidance',
    cost: 12,
    category: 'bonus',
    freeTierAvailable: false,
    subscriptionUnlimited: false
  }
]

// Helper functions
export const getFeatureCost = (featureId: string): FeatureCost | undefined => {
  return FEATURE_COSTS.find(feature => feature.id === featureId)
}

export const getFeaturesByCategory = (category: FeatureCost['category']): FeatureCost[] => {
  return FEATURE_COSTS.filter(feature => feature.category === category)
}

export const getFreeFeatures = (): FeatureCost[] => {
  return FEATURE_COSTS.filter(feature => feature.freeTierAvailable)
}

export const getPremiumFeatures = (): FeatureCost[] => {
  return FEATURE_COSTS.filter(feature => !feature.freeTierAvailable)
}

export const getTotalSubscriptionValue = (planId: string): number => {
  // This would be calculated based on the subscription plan
  // For now, return a base value
  const planValues: { [key: string]: number } = {
    'starter_7': 200,
    'premium_30': 800,
    'cosmic_90': 2500,
    'annual_365': 10000
  }
  return planValues[planId] || 0
}

// Feature cost validation
export const validateFeatureAccess = (
  featureId: string, 
  userCoins: number, 
  hasActiveSubscription: boolean
): { canAccess: boolean; reason?: string; cost?: number } => {
  const feature = getFeatureCost(featureId)
  if (!feature) {
    return { canAccess: false, reason: 'Feature not found' }
  }

  // Check if user has active subscription and feature is unlimited
  if (hasActiveSubscription && feature.subscriptionUnlimited) {
    return { canAccess: true, reason: 'Included in subscription' }
  }

  // Check if user has enough coins
  if (userCoins >= feature.cost) {
    return { canAccess: true, reason: 'Sufficient coins', cost: feature.cost }
  }

  return { 
    canAccess: false, 
    reason: `Insufficient coins. Need ${feature.cost} RRAASI coins.`,
    cost: feature.cost
  }
}
